import React, { useState, useMemo } from 'react'
import NiceModal from '@ebay/nice-modal-react'
import SearchPage from 'containers/SearchPage'
import { Chip, Button, Person, Table } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import useCurrentUser from 'hooks/useCurrentUser'
import useApi from 'hooks/useApi'
import AnnouncementCreateModal from './components/AnnouncementCreateModal'
import messages from './messages'

const USER_TYPE_LABELS = { 3: 'Supplier', 4: 'Customer' , 1: 'Coordinator'}

const CoordinatorView = ({ history, ...props }) => {
  const { formatMessage } = useIntl()
  const [refreshKey, setRefreshKey] = useState(0)

  const refresh = () => setRefreshKey((k) => k + 1)

  const handleDelete = (announcement) => {
    if (!window.confirm(`Delete "${announcement.title}"?`)) return
    fetch(`/rest/v2/announcement/${announcement.uid}`, { method: 'DELETE' })
      .then((r) => r.json())
      .then(({ success, errors }) => {
        if (!success) {
          toastr.error((errors || []).join(', '))
          return
        }
        refresh()
      })
  }

  const handleRowClick = (announcement) => {
    history.push(`/announcements/${announcement.uid}`)
  }

  return (
    <div className={props.match.isExact ? 'mt-2 w-full' : 'mt-2 w-full max-w-md'}>
      {props.match.isExact &&
      <div className="flex justify-end">
        <Button
          icon={['fas', 'plus']}
          onClick={() => NiceModal.show(AnnouncementCreateModal, { onSave: refresh })}
        >
          {formatMessage(messages.createAnnouncement)}
        </Button>
      </div>
      }
      <SearchPage
        key={refreshKey}
        {...props}
        baseApiUrl="/rest/v2/announcement"
        containerClassName="w-full"
        components={{
          Tr: ({ childProps = {}, ...innerProps }) => (
            <Table.Tr
              {...innerProps}
              onClick={childProps.uid ? () => handleRowClick(childProps) : undefined}
              className={{
                'cursor-pointer': !!childProps.uid,
                'bg-blue-50': !!(childProps.uid && location.pathname === `/announcement/${childProps.uid}`),
              }}
            />
          ),
        }}
        rowActions={[
          {
            state: 'negative',
            label: formatMessage(messages.deleteConfirm),
            onClick: handleDelete,
          },
        ]}
        head={{
          title: { label: formatMessage(messages.columnTitle), className: 'w-2/5' },
          usergroup: { label: formatMessage(messages.columnUserType), className: 'w-1/8' },
          partner_name: { label: formatMessage(messages.columnPartner), className: 'w-1/5' },
          tags: { label: formatMessage(messages.columnTags), className: 'w-1/5' },
          created_at: { label: formatMessage(messages.columnDate), className: 'w-1/8' },
        }}
        body={{
          title: (a) => <span className="text-sm font-medium">{a.title}</span>,
          usergroup: (a) => (
            <span className="text-sm text-gray-600">
              {USER_TYPE_LABELS[a.usergroup] ?? a.usergroup}
            </span>
          ),
          partner_name: (a) => <span className="text-sm text-gray-600">{a.partner_name}</span>,
          tags: (a) => (
            <div className="flex flex-wrap gap-1">
              {(a.tags || []).map((tag) => (
                <Chip key={tag} className="text-xs">{tag}</Chip>
              ))}
            </div>
          ),
          created_at: (a) => (
            <span className="text-sm text-gray-500">
              <FormattedDate value={new Date(a.created_at)} />
            </span>
          ),
        }}
      />
    </div>
  )
}

const CustomerView = () => {
  const { formatMessage } = useIntl()
  const [tags, setTags] = useState([])

  const [data] = useApi('/rest/v2/announcement')

  const announcements = useMemo(() => tags.length
    ? data.filter((a) => a.tags.some((tag) => tags.includes(tag)))
    : data
  , [tags, data])

  return (
    <div className="w-full h-full py-3 px-4 lg:py-5 lg:px-6 space-y-6">
      <div className="pb-5 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900 space-x-2">
          <FormattedMessage {...messages.title} />
        </h3>
        <p className="mt-2 max-w-4xl text-sm text-gray-500">
          <FormattedMessage {...messages.subtitle} />
        </p>
      </div>

      <div className="max-w-5xl divide-y divide-gray-200 m-auto">
        {announcements?.map((announcement) => (
          <article id={announcement.title} key={announcement.title} className="flex flex-col sm:flex-row gap-6 py-6">
            <div className="w-full sm:w-1/3 flex flex-col space-y-3">
              {!announcement.acked &&
                <div>
                  <Chip state="success">
                    <FormattedMessage {...messages.new} />
                  </Chip>
                </div>
              }

              <h3 className="text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                <a href={`#${announcement.title}`}>
                  {announcement.title}
                </a>
              </h3>

              <time dateTime={announcement.created_at} className="text-gray-500 text-sm">
                <FormattedDate value={new Date(announcement.created_at)} />
              </time>

              <div className="flex space-x-2">
                {announcement.tags?.map((tag) => (
                  <Chip
                    key={tag}
                    state={tags.includes(tag) ? 'info' : undefined}
                    onClick={() => setTags((prev) => tags.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])}
                  >
                    {tag}
                  </Chip>
                ))}
              </div>

              {!!announcement.labs?.length &&
                <div>
                  <Link to="/settings/features">
                    <Button variant="inline" icon={['fas', 'arrow-right']} iconPosition="right">
                      <FormattedMessage {...messages.featureFlagLink} />
                    </Button>
                  </Link>
                </div>
              }
            </div>

            <div className="w-full sm:w-2/3">
              <div>
                <ReactMarkdown
                  children={announcement.body}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    img: ({ node, ...props }) => <img className="border border-gray-400 shadow-sm my-8 max-w-full" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc" {...props} />,
                    p: ({ node, ...props }) => <p className="mb-3" {...props} />,
                  }}
                />
              </div>

              {announcement.created_by &&
                <div className="mt-10">
                  <Person {...announcement.created_by} />
                </div>
              }
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}

const AnnouncementsPage = ({ history, ...props }) => {
  const { roles } = useCurrentUser()
  const isCoordinator = roles.includes('coordinator')

  return isCoordinator ? <CoordinatorView history={history} {...props} /> : <CustomerView />
}

export default AnnouncementsPage
